import React from 'react';
import {
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    List,
    ListItem,
    ListItemAvatar,
    ListItemText,
    Stack,
    Tooltip,
    Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import {useSession} from '../../../../../firebase/UserProvider';
import {getAllUsers, searchUsersByName} from '../../../../../firebase/firestore/users';
import {
    addDataItemShare,
    getDataItemShares,
    removeDataItemShare,
} from '../../../../../utils/api/dataItemAccess';
import UserAvatar from '../../../../../components/UserAvatar';
import SearchUsers from '../../reportHeader/SearchUsers';

function recipientLabel(r) {
    if (!r) return '';
    return (
        r.displayName
        || [r.firstName, r.lastName].filter(Boolean).join(' ').trim()
        || r.email
        || r.userId
    );
}

function toAvatarUser(r) {
    if (!r) return null;
    return {
        id: r.userId,
        userId: r.userId,
        displayName: r.displayName,
        email: r.email,
        hasAvatar: r.hasAvatar,
        profilePicture: r.profilePicture,
    };
}

/**
 * Per-user, view-only share dialog for a data item (My Files), modeled on the report ShareDialog.
 * Data-item sharing has no roles, so the recipient list is name + email + Remove only.
 */
export default function DataItemShareDialog({open, onClose, dataItem}) {
    const {user: currentUser} = useSession();
    const currentUserId = currentUser?.uid || currentUser?.id || null;

    const dataItemId = dataItem?.id ?? dataItem?.canonicalItemId ?? dataItem?.referenceId ?? null;
    const dataItemName = dataItem?.name || dataItem?.title || 'this file';

    const [share, setShare] = React.useState(null); // DataItemShareResponse
    const [loading, setLoading] = React.useState(false);
    const [error, setError] = React.useState(null);

    // add-people search state
    const [query, setQuery] = React.useState('');
    const [debouncing, setDebouncing] = React.useState(false);
    const [searchLoading, setSearchLoading] = React.useState(false);
    const [results, setResults] = React.useState([]);
    const [searchError, setSearchError] = React.useState(null);

    const refetch = React.useCallback(async (signal) => {
        if (!dataItemId || !currentUserId) return;
        setLoading(true);
        setError(null);
        try {
            const data = await getDataItemShares({dataItemId, userId: currentUserId, signal});
            setShare(data);
        } catch (e) {
            if (e?.name !== 'AbortError') {
                setError('Could not load sharing for this file.');
                console.error('DataItemShareDialog load failed', e);
            }
        } finally {
            setLoading(false);
        }
    }, [dataItemId, currentUserId]);

    React.useEffect(() => {
        if (!open) {
            setShare(null);
            setQuery('');
            setResults([]);
            setError(null);
            setSearchError(null);
            return undefined;
        }
        const controller = new AbortController();
        refetch(controller.signal);
        return () => controller.abort();
    }, [open, refetch]);

    // ids already in the share (owner + recipients) — excluded from search results.
    const existingIds = React.useMemo(() => {
        const ids = new Set();
        if (share?.ownerUserId) ids.add(share.ownerUserId);
        (share?.recipients || []).forEach((r) => r?.userId && ids.add(r.userId));
        return ids;
    }, [share]);

    React.useEffect(() => {
        if (!open) return undefined;
        const q = (query || '').trim();
        let cancelled = false;
        setDebouncing(Boolean(q));
        setSearchError(null);

        const load = async () => {
            setSearchLoading(true);
            try {
                const found = q ? await searchUsersByName(q, {limit: 20}) : await getAllUsers(50);
                if (cancelled) return;
                setResults((found || []).filter((u) => u?.id && !existingIds.has(u.id)));
            } catch (e) {
                if (!cancelled) {
                    setResults([]);
                    setSearchError('Could not load users. Try again.');
                    console.error('DataItemShareDialog user search failed', e);
                }
            } finally {
                if (!cancelled) {
                    setSearchLoading(false);
                    setDebouncing(false);
                }
            }
        };

        const t = q ? setTimeout(load, 300) : load();
        return () => {
            cancelled = true;
            if (typeof t === 'number') clearTimeout(t);
        };
    }, [open, query, existingIds]);

    const handleAddUser = async (userObj) => {
        if (!userObj?.id || !dataItemId || !currentUserId) return;
        try {
            const updated = await addDataItemShare({
                dataItemId,
                shareWithUserId: userObj.id,
                userId: currentUserId,
            });
            setShare(updated);
            setQuery('');
            setResults([]);
            setSearchError(null);
        } catch (e) {
            setError(e?.message || 'Failed to share with this user.');
            console.error('Failed to add data item share', e);
        }
    };

    const handleRemove = async (sharedWithUserId) => {
        if (!sharedWithUserId || !dataItemId || !currentUserId) return;
        try {
            const updated = await removeDataItemShare({
                dataItemId,
                sharedWithUserId,
                userId: currentUserId,
            });
            setShare(updated);
        } catch (e) {
            setError(e?.message || 'Failed to revoke access.');
            console.error('Failed to remove data item share', e);
        }
    };

    const recipients = share?.recipients || [];
    const ownerSummary = share?.owner;

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
            <DialogTitle>Share “{dataItemName}”</DialogTitle>
            <DialogContent>
                <Stack spacing={2} sx={{mt: 1}}>
                    {error && (
                        <Typography variant="body2" color="error">{error}</Typography>
                    )}

                    <Stack>
                        <Typography variant="subtitle2" gutterBottom>Add people</Typography>
                        <SearchUsers
                            query={query}
                            setQuery={setQuery}
                            searchLoading={searchLoading || debouncing}
                            results={results}
                            searchError={searchError}
                            onAddUser={handleAddUser}
                        />
                    </Stack>

                    <Box>
                        <Typography variant="subtitle2" gutterBottom>People with access</Typography>
                        {loading ? (
                            <Stack alignItems="center" justifyContent="center" sx={{py: 3}}>
                                <CircularProgress/>
                            </Stack>
                        ) : (
                            <List dense>
                                {ownerSummary && (
                                    <ListItem sx={{px: 0.5}}>
                                        <ListItemAvatar>
                                            <UserAvatar user={toAvatarUser(ownerSummary)} userId={ownerSummary.userId}/>
                                        </ListItemAvatar>
                                        <ListItemText
                                            primary={recipientLabel(ownerSummary)}
                                            secondary={ownerSummary.email || ownerSummary.userId}
                                        />
                                        <Typography variant="body2" color="text.secondary">Owner</Typography>
                                    </ListItem>
                                )}
                                {recipients.map((r) => (
                                    <ListItem key={r.userId} sx={{px: 0.5}}>
                                        <ListItemAvatar>
                                            <UserAvatar user={toAvatarUser(r)} userId={r.userId}/>
                                        </ListItemAvatar>
                                        <ListItemText
                                            primary={recipientLabel(r)}
                                            secondary={r.email || r.userId}
                                        />
                                        <Tooltip title="Remove access">
                                            <IconButton size="small" aria-label="Remove access" onClick={() => handleRemove(r.userId)}>
                                                <CloseIcon fontSize="small"/>
                                            </IconButton>
                                        </Tooltip>
                                    </ListItem>
                                ))}
                                {!recipients.length && (
                                    <Typography variant="body2" color="text.secondary" sx={{px: 0.5, py: 1}}>
                                        Not shared with anyone yet.
                                    </Typography>
                                )}
                            </List>
                        )}
                    </Box>
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Done</Button>
            </DialogActions>
        </Dialog>
    );
}
